import React, { useState } from "react";
import Modal from "components/common/Modal";
import { Button } from "components/common";
import Notification from "lib/api/Notification";
import Client from "lib/api/Client";
import clientCache from "lib/utils/clientCache";

export default function ViewClientsModal({ show, onClose, clientsWithNotifications, loading, onClientDeleted, notification, onClientAdded }) {
  const [deleting, setDeleting] = useState(null);
  const [clientInput, setClientInput] = useState("");
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState("");
  const [addSuccess, setAddSuccess] = useState("");

  if (!show) {
    return null;
  }

  async function handleDelete(client) {
    const confirmDelete = window.confirm(
      `Are you sure you want to remove the notification for "${client.name}"? This action cannot be undone.`
    );

    if (!confirmDelete) {
      return;
    }

    setDeleting(client.notification_uuid);

    try {
      await Notification.delete(client.notification_uuid);

      // Call the parent's callback to refresh the data
      if (onClientDeleted) {
        await onClientDeleted();
      }
    } catch (exception) {
      console.error("Failed to delete notification:", exception);
      alert("Failed to delete notification. Please try again.");
    } finally {
      setDeleting(null);
    }
  }

  async function handleAddClient() {
    setAddError("");
    setAddSuccess("");

    const entries = clientInput
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);

    if (entries.length === 0) {
      setAddError("Please enter a client UUID or customer ID");
      return;
    }

    setAdding(true);

    const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    const failed = [];
    let successCount = 0;

    for (const entry of entries) {
      try {
        const isUuid = uuidPattern.test(entry);
        let client = null;

        if (isUuid) {
          const cachedClient = clientCache.get(entry);
          if (cachedClient) {
            client = cachedClient;
          } else {
            const response = await Client.get({ uuid: entry, fields: 'uuid,name,code,id' });
            if (response.data && response.data.length > 0) {
              client = response.data[0];
              clientCache.set(client.uuid, client);
            }
          }
        } else {
          const response = await Client.get({ code: entry, fields: 'uuid,name,code,id' });
          if (response.data && response.data.length > 0) {
            client = response.data[0];
            clientCache.set(client.uuid, client);
          }
        }

        if (!client) {
          failed.push(entry);
          continue;
        }

        const isDuplicate = clientsWithNotifications.some(c => c.uuid === client.uuid);
        if (isDuplicate) {
          failed.push(entry);
          continue;
        }

        if (onClientAdded) {
          await onClientAdded(client.uuid);
        }
        successCount++;
      } catch (exception) {
        console.error("Failed to add client:", entry, exception);
        failed.push(entry);
      }
    }

    setAdding(false);
    setClientInput(failed.join(", "));

    if (successCount > 0 && failed.length === 0) {
      setAddSuccess(`${successCount} client${successCount > 1 ? 's' : ''} successfully added`);
    } else if (successCount > 0 && failed.length > 0) {
      setAddSuccess(`${successCount} client${successCount > 1 ? 's' : ''} added`);
      setAddError(`${failed.length} entr${failed.length > 1 ? 'ies' : 'y'} could not be added and remain in the input`);
    } else {
      setAddError("No clients could be added. Please verify the UUIDs or customer IDs");
    }
  }

  return (
    <Modal show={show}>
      <div className="app-body__page">
        <h1 className="m-bot-10">Clients</h1>
        <div className="hr hr--darkest m-bot-20"></div>

        <div className="m-bot-20">
          <label>
            <span className="label__span">Add Clients by UUID or Customer ID (comma-separated)</span>
            <div className="row middle-xs">
              <div className="col-xs-8">
                <input
                  type="text"
                  value={clientInput}
                  onChange={(e) => setClientInput(e.target.value)}
                  placeholder="Enter one or more UUIDs or customer IDs, comma-separated"
                  disabled={adding || loading}
                />
              </div>
              <div className="col-xs-4">
                <Button
                  className="button button--small"
                  onClick={handleAddClient}
                  disabled={adding || loading || !clientInput.trim()}
                >
                  {adding ? 'Adding...' : 'Add Client'}
                </Button>
              </div>
            </div>
          </label>
          {addError && (
            <div className="m-top-10" style={{ color: 'red' }}>
              {addError}
            </div>
          )}
          {addSuccess && (
            <div className="m-top-10" style={{ color: 'green' }}>
              {addSuccess}
            </div>
          )}
        </div>

        <div className="hr hr--darkest m-bot-20"></div>

        {loading ? (
          <div className="m-bot-20">
            <p>Loading clients...</p>
          </div>
        ) : (
          <>
            {clientsWithNotifications && clientsWithNotifications.length > 0 ? (
              <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                <table className="table">
                  <thead>
                    <tr>
                      <th><strong>Client / Target UUID</strong></th>
                      <th><strong>Code</strong></th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {clientsWithNotifications.map((client) => (
                      <tr key={client.uuid}>
                        <td>{client.name || client.target_uuid}</td>
                        <td>{client.code || '-'}</td>
                        <td style={{ textAlign: 'right' }}>
                          <Button
                            className="button button--danger button--small"
                            onClick={() => handleDelete(client)}
                            disabled={deleting === client.notification_uuid}
                          >
                            {deleting === client.notification_uuid ? 'Deleting...' : 'Delete'}
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="m-bot-20">
                <p>No clients found for this notification.</p>
              </div>
            )}
          </>
        )}

        <div className="hr hr--darkest m-top-10 m-bot-20"></div>
        <div className="row middle-xs">
          <div className="col-xs-4">
            <Button className="button" onClick={onClose}>
              Close
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
